// @flow
import React, { PureComponent } from 'react';
import 'app/common/react/components/growth/activation/NuxInterest/NuxInterest.scss';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import InterestImage from 'app/common/react/components/growth/activation/NuxInterest/InterestImage';
import { Experiment } from 'app/common/react/components/experiments';
import { generateKeyframesCSS } from 'app/common/react/components/growth/activation/animations/animationsUtils';
import { type InterestObj } from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { Box, Icon, Image, Mask, Text, Touchable } from 'gestalt';

type Props = {|
  clickHandler: () => void,
  flipText: string,
  interest: InterestObj,
  isSelected: boolean,
  isTopicEdu?: boolean,
  isCategorized?: boolean,
  shouldFlip: boolean, // Used to make sure cards only flip right after select/deselect
|};

type State = {|
  isHover: boolean,
|};

type checkmarkColor = 'white' | 'gray';

const checkmark = (color: checkmarkColor = 'white') => (
  <Box position="absolute" right marginTop={2} marginRight={2} marginLeft={2} top>
    <Icon accessibilityLabel={i18n._('selected')} color={color} icon="check-circle" size={24} />
  </Box>
);

// Flip animation properties
const FLIP_DURATION_SEC = 0.225;
const keyframes = [
  `flipToBack {
    0% { transform: rotateY(0) }
    50% { transform: rotateY(90deg) }
    100% { transform: rotateY(90deg) }
}`,
  `flipToFront {
    0% { transform: rotateY(90deg) }
    50% { transform: rotateY(90deg) }
    100% { transform: rotateY(0) }`,
];

// The card flipping animation is made by overlapping both sides of the card and animating both
// The front side does "flipToBack", and the back side does "flipToFront"
const animations = {
  flipToBack: 'flipToBack ' + FLIP_DURATION_SEC + 's linear both',
  flipToFront: 'flipToFront ' + FLIP_DURATION_SEC + 's linear both',
};

// TODO (dkuokka, justinzhang):
// remove experiment/experience checks (standardize props) once
// ship/shutdown gestalt experiment and/or categorized renux experiment

class NuxInterest extends PureComponent<Props, State> {
  state = {
    isHover: false,
  };

  toggleMouseHover(isHoverInput: boolean) {
    this.setState({
      isHover: this.props.isTopicEdu ? false : isHoverInput,
    });
  }

  render() {
    const { isCategorized, isSelected, isTopicEdu, shouldFlip } = this.props;

    let image_size = 136;
    let text_size = 'lg';
    if (isTopicEdu) {
      image_size = 100;
      text_size = 'sm';
    } else if (isCategorized) {
      image_size = 110;
      text_size = 'md';
    }

    return (
      <Experiment name="web_nux_picker_flip">
        {({ anyEnabled: anyEnabledFlip, group: flipGroup }) => (
          <Box position="relative" width={image_size} height={image_size}>
            {/* Both sides of card are overlapping */}
            <Box position="absolute" top left>
              {/* Only exists if flipping: start on this side, opposite of goal. */ shouldFlip &&
                (anyEnabledFlip && flipGroup !== 'enabled_text') &&
                this.renderSide(
                  !isSelected, // If flipping, this represents the opposite of the goal state
                  animations.flipToBack, // This card starts in the front, and flips to the back
                  anyEnabledFlip,
                  image_size,
                  text_size,
                )}
            </Box>
            <Box position="absolute" top left>
              {/* Show this side (if not flipping), or animate towards this side */ this.renderSide(
                isSelected,
                shouldFlip && (anyEnabledFlip && flipGroup !== 'enabled_text')
                  ? animations.flipToFront // This card starts in the back and flips to the front
                  : undefined, // otherwise no animation
                anyEnabledFlip,
                image_size,
                text_size,
              )}
            </Box>
          </Box>
        )}
      </Experiment>
    );
  }

  renderSide(
    isSelected: boolean,
    currentAnimation: ?string,
    showFlipMessage: boolean,
    image_size: number,
    text_size: 'sm' | 'md' | 'lg',
  ) {
    const { clickHandler, flipText, interest, isTopicEdu, isCategorized } = this.props;
    const { isHover } = this.state;
    const { background_color, images, label } = interest;

    const image = images['400x'];
    let overlay = 'rgba(0, 0, 0, 0.1)';
    if (isSelected) {
      overlay = 'rgba(0, 0, 0, 0.7)';
    } else if (isHover) {
      overlay = 'rgba(0, 0, 0, 0.6)';
    }

    return (
      <Box
        dangerouslySetInlineStyle={{
          __style: {
            transformStyle: 'preserve-3d',
            animation: currentAnimation,
            WebkitAnimation: currentAnimation,
          },
        }}
      >
        {/* eslint-disable  react/no-danger */}
        <style
          dangerouslySetInnerHTML={{
            __html: generateKeyframesCSS(keyframes),
          }}
        />
        {/* eslint-enable  react/no-danger */}
        <Mask shape="rounded">
          <Experiment name="web_nux_darker_topic_opacity">
            {({ anyEnabled: darkenOpacity }) => (
              <Experiment name="web_gestalt_interest">
                {({ anyEnabled }) =>
                  anyEnabled ? (
                    <Touchable
                      mouseCursor="pointer"
                      onTouch={clickHandler}
                      onMouseEnter={() => this.toggleMouseHover(true)}
                      onMouseLeave={() => this.toggleMouseHover(false)}
                      shape="rounded"
                    >
                      <Box
                        dangerouslySetInlineStyle={{
                          __style: {
                            // topics are not clickable when on top of homefeed
                            // cannot override via touchable component
                            cursor: isTopicEdu && 'default',
                          },
                        }}
                        data-test-id="nux-picker-topic"
                        height={image_size}
                        position="relative"
                        shape="rounded"
                        width={image_size}
                      >
                        <Image
                          alt={label}
                          color={background_color}
                          fit="cover"
                          naturalHeight={image.height}
                          naturalWidth={image.width}
                          src={image.url}
                        >
                          <Box
                            dangerouslySetInlineStyle={{
                              __style: {
                                backgroundColor: darkenOpacity ? 'rgba(0, 0, 0, 0.3)' : overlay,
                              },
                            }}
                            height="100%"
                            position="absolute"
                            shape="rounded"
                            top
                            width="100%"
                          >
                            {isSelected && checkmark('white')}
                            <Box
                              bottom
                              data-test-id="nux-picker-topic-label"
                              margin={2}
                              position="absolute"
                            >
                              {isSelected &&
                                showFlipMessage && (
                                  <Box marginBottom={2}>
                                    <Text align="left" color="white" size="sm">
                                      {flipText}
                                    </Text>
                                  </Box>
                                )}
                              <Text align="left" bold color="white" size={text_size}>
                                {label}
                              </Text>
                            </Box>
                          </Box>
                        </Image>
                      </Box>
                    </Touchable>
                  ) : (
                    <Box shape="rounded" position="relative" display="inlineBlock">
                      <button
                        aria-selected={isSelected}
                        className={cx(
                          'NuxInterest',
                          { topicEdu: isTopicEdu },
                          { categorized: isCategorized },
                        )}
                        data-test-id="nux-picker-topic"
                        onClick={clickHandler}
                        onMouseEnter={() => this.toggleMouseHover(true)}
                        onMouseLeave={() => this.toggleMouseHover(false)}
                        role={'option'}
                      >
                        <div className={cx('interestCardWrapper')}>
                          <Box
                            position="absolute"
                            bottom
                            margin={2}
                            data-test-id="nux-picker-topic-label"
                          >
                            {isSelected &&
                              showFlipMessage && (
                                <Box marginBottom={2}>
                                  <Text align="left" color="white" size="sm">
                                    {flipText}
                                  </Text>
                                </Box>
                              )}
                            <Text align="left" bold color="white" size={text_size}>
                              {label}
                            </Text>
                          </Box>
                          {isSelected && (
                            <div className={cx('selectedWrapper')}>
                              {checkmark('white')}
                              <div className="selectedOverlay" />
                            </div>
                          )}
                          {!isSelected &&
                            !isTopicEdu && (
                              <div>
                                <div className={cx('hoverText')}>
                                  <Text bold color="white" align="left" size={text_size}>
                                    {label}
                                  </Text>
                                </div>
                                <div className={cx('hoverMask')} />
                              </div>
                            )}
                        </div>
                        <InterestImage
                          color={background_color}
                          image={image}
                          label={label}
                          inOpacityExp={darkenOpacity}
                        />
                      </button>
                    </Box>
                  )
                }
              </Experiment>
            )}
          </Experiment>
        </Mask>
      </Box>
    );
  }
}

export default NuxInterest;
